import { useState, useEffect, useRef } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useChat } from '../../context/ChatContext'
import { TrashIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import ConfirmDialog from '../ConfirmDialog'
import toast from 'react-hot-toast'
import notificationService from '../../services/notificationService'

const Sidebar = ({ onClose }) => {
  const { user, logout } = useAuth()
  const { conversations, loadConversations, deleteConversation } = useChat()
  const navigate = useNavigate()
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [conversationToDelete, setConversationToDelete] = useState(null)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [unreadCount, setUnreadCount] = useState(0)
  const dropdownRef = useRef(null)

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationService.getNotifications()
        const notifications = response.data || []
        const count = notifications.filter(n => !n.isRead).length
        setUnreadCount(count)
      } catch (error) {
        console.error('Error fetching notifications:', error)
      }
    }
    
    fetchUnreadCount()
    
    // Refresh count every minute
    const interval = setInterval(fetchUnreadCount, 60000)
    return () => clearInterval(interval)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: '🏠' },
    { name: 'Chat', path: '/chat', icon: '💬' },
    { name: 'Health', path: '/health', icon: '🏥' },
    { name: 'Journal', path: '/journal', icon: '📝' },
    { name: 'Notifications', path: '/notifications', icon: '🔔', badge: true },
    { name: 'Faces', path: '/faces', icon: '👤' },
    { name: 'Events', path: '/events', icon: '📅' },
    { name: 'Profile', path: '/profile', icon: '⚙️' },
  ]

  const handleDeleteClick = (conversation, e) => {
    e.preventDefault()
    e.stopPropagation()
    setConversationToDelete(conversation)
    setDeleteDialogOpen(true)
    setActiveDropdown(null)
  }

  const handleDeleteConfirm = async () => {
    if (conversationToDelete) {
      try {
        await deleteConversation(conversationToDelete._id)
        toast.success('Conversation deleted successfully')
        
        // If we're currently viewing this conversation, navigate to chat home
        if (window.location.pathname.includes(conversationToDelete._id)) {
          navigate('/chat')
        }
      } catch (error) {
        toast.error('Failed to delete conversation')
      }
    }
  }

  const toggleDropdown = (conversationId, e) => {
    e.preventDefault()
    e.stopPropagation()
    setActiveDropdown(activeDropdown === conversationId ? null : conversationId)
  }

  return (
    <div className="bg-white w-64 shadow-lg flex flex-col h-full">
      {/* Logo/Brand with close button for mobile */}
      <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-gray-800">AI Assistant</h1>
          <p className="text-xs sm:text-sm text-gray-600 truncate">Welcome, {user?.name}</p>
        </div>
        <button 
          onClick={onClose}
          className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 sm:p-4">
        <ul className="space-y-1 sm:space-y-2">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                onClick={onClose} // Close sidebar on mobile when navigating
                className={({ isActive }) =>
                  `flex items-center justify-between px-3 sm:px-4 py-2 rounded-lg transition-colors duration-200 ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                  }`
                }
              >
                <div className="flex items-center">
                  <span className="mr-2 sm:mr-3 text-base sm:text-lg">{item.icon}</span>
                  <span className="text-sm sm:text-base">{item.name}</span>
                </div>
                {item.badge && unreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Recent Conversations */}
        <div className="mt-4 sm:mt-8">
          <h3 className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wider">
            Recent Chats
          </h3>
          <ul className="mt-2 space-y-1">
            {conversations.slice(0, 5).map((conversation) => (
              <li key={conversation._id} className="relative group">
                <div className="flex items-center">
                  <NavLink
                    to={`/chat/${conversation._id}`}
                    onClick={onClose} // Close sidebar on mobile when navigating
                    className="flex-1 block px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-800 rounded-lg transition-colors duration-200 truncate pr-8"
                  >
                    {conversation.title}
                  </NavLink>
                  
                  {/* Dropdown Menu Button */}
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={(e) => toggleDropdown(conversation._id, e)}
                      className="opacity-0 group-hover:opacity-100 absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-opacity duration-200"
                    >
                      <EllipsisVerticalIcon className="h-4 w-4" />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {activeDropdown === conversation._id && (
                      <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                        <button
                          onClick={(e) => handleDeleteClick(conversation, e)}
                          className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200"
                        >
                          <TrashIcon className="h-4 w-4 mr-2" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          
          {/* View All Conversations Link */}
          {conversations.length > 5 && (
            <div className="mt-3">
              <NavLink
                to="/chat/all"
                onClick={onClose} // Close sidebar on mobile when navigating
                className="block px-3 sm:px-4 py-2 text-xs sm:text-sm text-primary-600 hover:text-primary-800 rounded-lg transition-colors duration-200"
              >
                View all conversations ({conversations.length})
              </NavLink>
            </div>
          )}
        </div>
      </nav>

      {/* Logout */}
      <div className="p-3 sm:p-4 border-t border-gray-200">
        <button
          onClick={logout}
          className="w-full flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
        >
          <span className="mr-2 sm:mr-3">🚪</span>
          <span>Logout</span>
        </button>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Conversation"
        message={`Are you sure you want to delete "${conversationToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default Sidebar
